import { SeverityNumber, logs, type AnyValueMap, type Logger as OtelLogger } from '@opentelemetry/api-logs';

import { flattenErrorAttribute } from './format-error';
import { LogLevel, type ILogger, type LogAttributes, type LogErrorOptions, type OnErrorLogged } from './types';

const SEVERITY_BY_LEVEL: Record<LogLevel, { number: SeverityNumber; text: string }> = {
  [LogLevel.ERROR]: { number: SeverityNumber.ERROR, text: 'ERROR' },
  [LogLevel.WARN]: { number: SeverityNumber.WARN, text: 'WARN' },
  [LogLevel.INFO]: { number: SeverityNumber.INFO, text: 'INFO' },
  [LogLevel.DEBUG]: { number: SeverityNumber.DEBUG, text: 'DEBUG' },
};

interface OtlpLoggerOptions {
  serviceName: string;
  onErrorLogged?: OnErrorLogged;
}

export class OtlpLogger implements ILogger {
  private readonly otelLogger: OtelLogger;

  private readonly onErrorLogged?: OtlpLoggerOptions['onErrorLogged'];

  constructor({ serviceName, onErrorLogged }: OtlpLoggerOptions) {
    this.otelLogger = logs.getLogger(serviceName);
    this.onErrorLogged = onErrorLogged;
  }

  private emit(level: LogLevel, message: string, attributes?: LogAttributes): void {
    const { number, text } = SEVERITY_BY_LEVEL[level];
    this.otelLogger.emit({
      severityNumber: number,
      severityText: text,
      body: message,
      attributes: attributes as AnyValueMap | undefined,
    });
  }

  error(message: string, attributes?: LogAttributes, { notifyExternally = true }: LogErrorOptions = {}): void {
    const { attributes: enriched, rawError } = flattenErrorAttribute(attributes);
    this.emit(LogLevel.ERROR, message, enriched);

    if (notifyExternally) {
      this.onErrorLogged?.(message, enriched, rawError);
    }
  }

  warn(message: string, attributes?: LogAttributes): void {
    this.emit(LogLevel.WARN, message, attributes);
  }

  info(message: string, attributes?: LogAttributes): void {
    this.emit(LogLevel.INFO, message, attributes);
  }

  debug(message: string, attributes?: LogAttributes): void {
    this.emit(LogLevel.DEBUG, message, attributes);
  }
}
